import { createRoute, type AnyRoute } from "@tanstack/react-router";
import { FileSignature } from "lucide-react";

import type { FeatureModule } from "@/features/types";
import { guarded } from "@/lib/auth";

import { ContractsPage } from "./pages/ContractsPage";

export type ContractsTabKey =
  | "contracts"
  | "accounts"
  | "companies"
  | "quickbooks"
  | "items"
  | "packages"
  | "discounts"
  | "contract-types"
  | "price-metrics";

/** Detail kinds that live in the URL (so cross-navigation contract→account→
 *  company is back-button friendly, and Contract Types' expanded playbook is
 *  shareable). The remaining tabs' details are local state. */
export type DetailKind = "contracts" | "accounts" | "companies" | "contract-types";

/** Search params on /contracts. The active tab AND the open record live in the
 *  URL so the view is shareable and the browser back button steps through both
 *  tab changes and contract→account→company navigation. */
export interface ContractsSearch {
  tab?: ContractsTabKey;
  /** Open record as "<kind>:<id>", kind ∈ DetailKind. */
  detail?: string;
  expanded?: boolean;
}

const TAB_KEYS: ContractsTabKey[] = [
  "contracts",
  "accounts",
  "companies",
  "quickbooks",
  "items",
  "packages",
  "discounts",
  "contract-types",
  "price-metrics",
];

const DETAIL_KINDS: DetailKind[] = ["contracts", "accounts", "companies", "contract-types"];

function validateContractsSearch(raw: Record<string, unknown>): ContractsSearch {
  const out: ContractsSearch = {};
  if (typeof raw.tab === "string" && (TAB_KEYS as string[]).includes(raw.tab)) {
    out.tab = raw.tab as ContractsTabKey;
  }
  if (typeof raw.detail === "string") {
    const kind = raw.detail.split(":")[0] ?? "";
    if ((DETAIL_KINDS as string[]).includes(kind) && raw.detail.length > kind.length + 1) {
      out.detail = raw.detail;
    }
  }
  if (raw.expanded === true || raw.expanded === "1" || raw.expanded === "true") {
    out.expanded = true;
  }
  return out;
}

export const contractsModule: FeatureModule = {
  key: "contracts",
  nav: {
    label: "Contracts",
    icon: FileSignature,
    to: "/contracts",
    requires: "contracts.read",
    order: 20,
    group: "Revenue",
  },
  routes: (rootRoute) => [
    createRoute({
      getParentRoute: () => rootRoute,
      path: "/contracts",
      component: guarded("contracts.read", ContractsPage),
      validateSearch: validateContractsSearch,
    }),
  ] as unknown as AnyRoute[],
};
